// src/components/auction/MantraSubstitutionEngine.js
// Simulatore delle sostituzioni Mantra, fedele alle 3 modalità ufficiali
// (fonte: https://www.fantacalcio.it/regolamenti/sistema-mantra, §5):
//   BASIC  → 1) stesso modulo senza malus  2) altro modulo senza malus (cambio
//            modulo)  3) qualsiasi modulo con malus, nessuna priorità del
//            modulo base — a parità di rango vince l'ordine panchina, tra le
//            soluzioni adattate vince prima il numero di malus più basso
//   EASY   → il modulo non cambia mai: 1) stesso modulo senza malus
//            2) stesso modulo con malus
//   MASTER → nessuna priorità di modulo fin da subito: per ogni panchinaro,
//            nell'ordine, si prova qualsiasi modulo senza malus prima di
//            passare al successivo; solo se nessuno trova una soluzione senza
//            malus si passa alle soluzioni adattate
// In tutte le modalità il portiere ha sempre la priorità assoluta di
// sostituzione (nessun ruolo può sostituirlo, mai con malus: §5.1).
//
// A differenza di un semplice controllo "il ruolo del subentrante combacia
// con lo slot vacante?", il regolamento (nota a fondo §5.2.1.1) prevede che
// il sistema rialloca l'intero undici (titolari rimasti + subentrante) sui
// ruoli disponibili: un giocatore multiruolo può quindi spostarsi di slot
// per lasciare libero il posto "giusto" al subentrante, mantenendo la
// soluzione senza malus. matchSlotsToPlayers implementa questa riallocazione
// completa come un problema di min-cost max-flow (costo 0 = ruolo diretto,
// costo 1 = ruolo assimilato/malus): un semplice augmenting-path "diretti
// prima, malus se serve" non basta, perché nella fase a malus può comunque
// convertire in malus alcuni abbinamenti diretti già trovati invece di
// limitarsi al minimo indispensabile — va quindi minimizzato il costo totale,
// non solo massimizzata la copertura.
//
// I ruoli assimilati per ciascuna composizione di slot (chi può coprirla con
// malus −1) sono presi dalla tabella ufficiale "Tabella sostituzioni per
// schema" (PDF linkato da §5.4 della pagina sopra), non da una mappa
// ruolo→ruolo generica: il regolamento infatti concede assimilazioni diverse
// a seconda che lo slot sia "puro" o combinato (es. una T pura ammette una W
// con malus, ma una T dentro uno slot T/A/Pc pure sì — mentre una W pura
// ammette M/C/E con malus ma NON una T, e viceversa; vedi eccezione 4-1-4-1
// sotto). MANTRA_SLOT_MALUS_MAP è quindi indicizzata per composizione di
// slot (i ruoli "naturali" ammessi in quello slot), non per singolo ruolo.
import { MANTRA_PRESETS } from './MantraRoleConfiguration';

export const MODE_LABELS = { basic: 'Base', easy: 'Facile', master: 'Master' };

const defaultRolesOf = (p) => p?.roles || [];

export function getFlatSlots(presetKey) {
  const preset = MANTRA_PRESETS[presetKey];
  if (!preset) return [];

  const slots = [];
  preset.rows.forEach((row) => {
    row.slots.forEach((options, slotIndex) => {
      slots.push({ rowKey: row.key, rowTitle: row.title, slotIndex, options, label: options.join('/') });
    });
  });
  return slots;
}

function slotKey(options) {
  return [...options].sort().join('/');
}

// Ruoli assimilati (malus −1) per ciascuna composizione di slot presente nei
// moduli Mantra, così come riportati nella tabella ufficiale delle
// sostituzioni. Non distingue "-1" da "-1*" (quest'ultimo vieta solo lo
// schieramento manuale in fase di formazione, §5.3, ma resta ammesso con
// malus a sostituzione avvenuta: §5.3 "Pur rimanendo possibile durante le
// sostituzioni... esclusivamente in fase di inserimento formazione sarà
// inibita la possibilità di...").
const RAW_SLOT_MALUS_MAP = {
  P: [],
  Dc: ['Dd', 'Ds', 'B'],
  'Dc/B': ['Dd', 'Ds'],
  Ds: ['Dd', 'Dc', 'B'],
  Dd: ['Ds', 'Dc', 'B'],
  E: ['Dd', 'Ds', 'Dc', 'B', 'M'],
  'E/W': ['Dd', 'Ds', 'Dc', 'B', 'M', 'C', 'T'],
  M: ['Dd', 'Ds', 'Dc', 'B', 'E'],
  'M/C': ['Dd', 'Ds', 'Dc', 'B', 'E'],
  C: ['Dd', 'Ds', 'Dc', 'B', 'E', 'M'],
  'C/T': ['Dd', 'Ds', 'Dc', 'B', 'E', 'M'],
  T: ['Dd', 'Ds', 'Dc', 'B', 'E', 'M', 'C', 'W'],
  'T/A': ['Dd', 'Ds', 'Dc', 'B', 'E', 'M', 'C', 'W'],
  'T/A/Pc': ['Dd', 'Ds', 'Dc', 'B', 'E', 'M', 'C', 'W'],
  W: ['Dd', 'Ds', 'Dc', 'B', 'E', 'M', 'C'],
  'W/A': ['Dd', 'Ds', 'Dc', 'B', 'E', 'M', 'C', 'T'],
  'W/T': ['Dd', 'Ds', 'Dc', 'B', 'E', 'M', 'C'],
  'A/Pc': ['Dd', 'Ds', 'Dc', 'B', 'E', 'M', 'C', 'T', 'W'],
};

const SLOT_MALUS_MAP = Object.fromEntries(
  Object.entries(RAW_SLOT_MALUS_MAP).map(([k, v]) => [slotKey(k.split('/')), v])
);

// Unica eccezione esplicita della tabella ufficiale: nel modulo 4-1-4-1 le W
// non possono mai sostituire le T (né viceversa), nemmeno con malus — a
// differenza di tutti gli altri moduli dove sono assimilabili.
const MODULE_SLOT_MALUS_EXCEPTIONS = {
  4141: { [slotKey(['T'])]: ['Dd', 'Ds', 'Dc', 'B', 'E', 'M', 'C'] },
};

function getMalusRoles(options, moduleKey) {
  const key = slotKey(options);
  const override = MODULE_SLOT_MALUS_EXCEPTIONS[moduleKey]?.[key];
  return override || SLOT_MALUS_MAP[key] || [];
}

function hasDirectEdge(options, roles) {
  return options.some((t) => roles.includes(t));
}

function hasMalusEdge(options, roles, moduleKey) {
  return getMalusRoles(options, moduleKey).some((r) => roles.includes(r));
}

// Min-cost max-flow (successive shortest augmenting path, SPFA): usato per
// trovare la copertura slot<->giocatori con il minor numero di malus
// possibile. Un semplice augmenting-path "in due fasi" (prima solo archi
// diretti, poi anche quelli a malus) NON basta: nella seconda fase può
// comunque imboccare una catena che converte in malus alcuni abbinamenti
// diretti già trovati, invece di limitarsi al minimo indispensabile. Il
// costo (0 = diretto, 1 = malus) va quindi minimizzato esplicitamente.
const FLOW_INFINITY = Number.POSITIVE_INFINITY;

class MinCostFlow {
  constructor(nodeCount) {
    this.graph = Array.from({ length: nodeCount }, () => []);
    this.edges = [];
  }

  addEdge(u, v, cap, cost) {
    this.graph[u].push(this.edges.length);
    this.edges.push({ to: v, cap, cost, flow: 0 });
    this.graph[v].push(this.edges.length);
    this.edges.push({ to: u, cap: 0, cost: -cost, flow: 0 });
  }

  // Ogni chiamata instrada al più 1 unità di flusso (tutte le capacità sono
  // 0/1) lungo il percorso a costo minimo residuo; ripete finché non se ne
  // trova più uno o si raggiunge `maxAugmentations`. Ritorna il flusso totale.
  run(source, sink, maxAugmentations) {
    let totalFlow = 0;
    for (let iter = 0; iter < maxAugmentations; iter += 1) {
      const dist = new Array(this.graph.length).fill(FLOW_INFINITY);
      const inQueue = new Array(this.graph.length).fill(false);
      const prevEdge = new Array(this.graph.length).fill(-1);
      dist[source] = 0;
      const queue = [source];
      inQueue[source] = true;

      while (queue.length) {
        const u = queue.shift();
        inQueue[u] = false;
        for (const e of this.graph[u]) {
          const edge = this.edges[e];
          if (edge.cap - edge.flow <= 0) continue;
          const v = edge.to;
          if (dist[u] + edge.cost < dist[v]) {
            dist[v] = dist[u] + edge.cost;
            prevEdge[v] = e;
            if (!inQueue[v]) {
              queue.push(v);
              inQueue[v] = true;
            }
          }
        }
      }

      if (dist[sink] === FLOW_INFINITY) break;

      let v = sink;
      while (v !== source) {
        const e = prevEdge[v];
        this.edges[e].flow += 1;
        this.edges[e ^ 1].flow -= 1;
        v = this.edges[e ^ 1].to;
      }
      totalFlow += 1;
    }
    return totalFlow;
  }
}

// Copertura completa slot<->giocatori che minimizza il numero totale di
// malus: ogni slot ammette un arco a costo 0 verso i giocatori con ruolo
// diretto e, se allowMalus, un arco a costo 1 verso quelli assimilati
// (tabella ufficiale). Ritorna null se una copertura completa non è
// raggiungibile nemmeno con malus (quando allowMalus è true) o senza (quando
// è false). `moduleKey` seleziona le eventuali eccezioni specifiche del
// modulo (es. 4-1-4-1, vedi sopra).
export function matchSlotsToPlayers(slots, players, { allowMalus = true, rolesOf = defaultRolesOf, moduleKey } = {}) {
  const roleCache = players.map(rolesOf);
  const slotCount = slots.length;
  const playerCount = players.length;

  const source = 0;
  const slotBase = 1;
  const playerBase = 1 + slotCount;
  const sink = 1 + slotCount + playerCount;
  const mcf = new MinCostFlow(sink + 1);

  for (let s = 0; s < slotCount; s += 1) mcf.addEdge(source, slotBase + s, 1, 0);
  for (let p = 0; p < playerCount; p += 1) mcf.addEdge(playerBase + p, sink, 1, 0);

  for (let s = 0; s < slotCount; s += 1) {
    for (let p = 0; p < playerCount; p += 1) {
      if (hasDirectEdge(slots[s].options, roleCache[p])) {
        mcf.addEdge(slotBase + s, playerBase + p, 1, 0);
      } else if (allowMalus && hasMalusEdge(slots[s].options, roleCache[p], moduleKey)) {
        mcf.addEdge(slotBase + s, playerBase + p, 1, 1);
      }
    }
  }

  const flow = mcf.run(source, sink, slotCount);
  if (flow < slotCount) return null;

  const assignment = new Array(slotCount).fill(null);
  const malusFlags = new Array(slotCount).fill(false);
  for (let s = 0; s < slotCount; s += 1) {
    const usedEdge = mcf.graph[slotBase + s]
      .map((e) => mcf.edges[e])
      .find((edge) => edge.to >= playerBase && edge.to < sink && edge.flow > 0);
    if (usedEdge) {
      assignment[s] = players[usedEdge.to - playerBase];
      malusFlags[s] = usedEdge.cost === 1;
    }
  }
  const malusCount = malusFlags.filter(Boolean).length;

  return { assignment, malusFlags, malusCount };
}

// Simula chi entra dalla panchina (ordinata per priorità esplicita) se il
// titolare `absentId` è assente, secondo la modalità di sostituzione scelta.
// `slots`/`assignment` rappresentano l'undici REALE già scelto dall'utente
// (il campetto), non un undici ricalcolato: la simulazione parte da lì.
export function simulateAbsence({ presetKey, slots, assignment, benchOrder, absentId, mode = 'basic', rolesOf = defaultRolesOf }) {
  const slotIndex = assignment.findIndex((p) => p && String(p.id) === String(absentId));
  if (slotIndex === -1) return null;

  const vacantSlot = slots[slotIndex];
  const remainingStarters = assignment.filter((p, i) => i !== slotIndex && p);
  const isGoalkeeperSlot = vacantSlot.options.includes('P');

  const allModuleKeys = Object.keys(MANTRA_PRESETS);
  const otherModuleKeys = allModuleKeys.filter((k) => k !== presetKey);
  const slotsForModule = (moduleKey) => (moduleKey === presetKey ? slots : getFlatSlots(moduleKey));

  const buildResult = (candidate, moduleKey, moduleSlots, matchResult) => {
    const candidateSlotIndex = matchResult.assignment.findIndex((p) => p && p.id === candidate.id);
    return {
      candidate,
      malus: matchResult.malusFlags[candidateSlotIndex],
      malusCount: matchResult.malusCount,
      malusFlags: matchResult.malusFlags,
      moduleChanged: moduleKey !== presetKey,
      newPresetKey: moduleKey,
      resultSlots: moduleSlots,
      resultAssignment: matchResult.assignment,
    };
  };

  // Un solo modulo, ordine panchina: il primo panchinaro che copre l'intero
  // undici (titolari rimasti + lui) in quel modulo vince.
  const tryModule = (moduleKey, { allowMalus }) => {
    const moduleSlots = slotsForModule(moduleKey);
    for (const candidate of benchOrder) {
      const pool = [...remainingStarters, candidate];
      const matchResult = matchSlotsToPlayers(moduleSlots, pool, { allowMalus, rolesOf, moduleKey });
      if (matchResult) return buildResult(candidate, moduleKey, moduleSlots, matchResult);
    }
    return null;
  };

  // Ordine panchina primario, modulo secondario: per ogni panchinaro (in
  // ordine) prova ciascun modulo finché non trova una copertura completa.
  // Usato da MASTER, dove non esiste alcuna priorità del modulo base.
  const tryCandidateFirstAcrossModules = (moduleKeys, { allowMalus }) => {
    for (const candidate of benchOrder) {
      for (const moduleKey of moduleKeys) {
        const moduleSlots = slotsForModule(moduleKey);
        const pool = [...remainingStarters, candidate];
        const matchResult = matchSlotsToPlayers(moduleSlots, pool, { allowMalus, rolesOf, moduleKey });
        if (matchResult) return buildResult(candidate, moduleKey, moduleSlots, matchResult);
      }
    }
    return null;
  };

  // Soluzioni ADATTATE: nessuna priorità del modulo base (§5.2.1). Per ogni
  // panchinaro (ordine di preferenza) si cerca il modulo che gli costa meno
  // malus; tra i vari panchinari vince chi ha il totale malus più basso e, a
  // parità di malus, chi è più in alto in panchina.
  const tryBestAdapted = (moduleKeys) => {
    let best = null;
    for (const candidate of benchOrder) {
      let bestForCandidate = null;
      for (const moduleKey of moduleKeys) {
        const moduleSlots = slotsForModule(moduleKey);
        const pool = [...remainingStarters, candidate];
        const matchResult = matchSlotsToPlayers(moduleSlots, pool, { allowMalus: true, rolesOf, moduleKey });
        if (!matchResult) continue;
        if (!bestForCandidate || matchResult.malusCount < bestForCandidate.matchResult.malusCount) {
          bestForCandidate = { moduleKey, moduleSlots, matchResult };
        }
      }
      if (!bestForCandidate) continue;

      const candidateResult = buildResult(candidate, bestForCandidate.moduleKey, bestForCandidate.moduleSlots, bestForCandidate.matchResult);
      if (!best || candidateResult.malusCount < best.malusCount) {
        best = candidateResult;
      }
    }
    return best;
  };

  let result = null;

  if (mode === 'easy') {
    result = tryModule(presetKey, { allowMalus: false })
      || tryBestAdapted([presetKey]);
  } else if (mode === 'master') {
    result = tryCandidateFirstAcrossModules(allModuleKeys, { allowMalus: false })
      || tryBestAdapted(allModuleKeys);
  } else {
    result = tryModule(presetKey, { allowMalus: false });
    if (!result) {
      for (const key of otherModuleKeys) {
        result = tryModule(key, { allowMalus: false });
        if (result) break;
      }
    }
    if (!result) result = tryBestAdapted(allModuleKeys);
  }

  return { resolved: Boolean(result), isGoalkeeperSlot, vacantSlot, ...result };
}
